import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApi } from '../lib/api'
import type { Invoice, InvoiceStatus } from '../types'
import { INVOICE_STATUS_LABELS, ACTIVE_INVOICE_STATUSES, ARCHIVED_INVOICE_STATUSES } from '../types'

type InvoiceRow = Invoice & { order_reference?: string; client_name?: string }

export default function InvoicesPage() {
  const navigate = useNavigate()
  const { apiFetch } = useApi()
  const [invoices, setInvoices] = useState<InvoiceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all')
  useEffect(() => {
    apiFetch<{ invoices: InvoiceRow[] }>('/api/invoices')
      .then(({ invoices: inv }) => setInvoices(inv))
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (ARCHIVED_INVOICE_STATUSES.includes(inv.status)) return false
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false
      if (search) {
        const q = search.toLowerCase()
        if (
          !inv.invoice_number.toLowerCase().includes(q) &&
          !(inv.order_reference ?? '').toLowerCase().includes(q) &&
          !(inv.client_name ?? '').toLowerCase().includes(q)
        ) return false
      }
      return true
    })
  }, [invoices, search, statusFilter])

  function fmtEuro(amount: number) {
    return amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
  }

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Rechnungen</h1>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Rechnungsnummer, Auftrag oder Kunde…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
          style={{ width: 160 }}
        >
          <option value="all">Alle aktiven</option>
          {ACTIVE_INVOICE_STATUSES.map((s) => (
            <option key={s} value={s}>{INVOICE_STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Rechnungen werden geladen…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Rechnung #</th>
                <th>Auftrag</th>
                <th>Kunde</th>
                <th>Betrag</th>
                <th>Verschickt</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                    Keine Rechnungen gefunden
                  </td>
                </tr>
              )}
              {filtered.map((inv) => (
                <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/invoices/${inv.id}`)}>
                  <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                  <td>
                    <Link to={`/orders/${inv.order_id}`}>
                      {inv.order_reference ?? `Auftrag #${inv.order_id}`}
                    </Link>
                  </td>
                  <td style={{ fontSize: 13 }}>{inv.client_name ?? '–'}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtEuro(inv.total_amount)}</td>
                  <td>{new Date(inv.sent_at).toLocaleDateString('de-DE')}</td>
                  <td>
                    <span className={`badge badge-${inv.status}`}>
                      {INVOICE_STATUS_LABELS[inv.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
